import React from 'react';
import { Link } from '@tanstack/react-router';
import { Box, Flex, Select, Text, TextField, type SelectOption } from '@chatsdet/ui';
import { Warning } from '@phosphor-icons/react';
import type { LinkedGitHubRepo } from '../../../../features/releases/releaseTypes';

export interface RepoPickerProps {
  /** Repos already linked to this property through the GitHub App. */
  repos: LinkedGitHubRepo[];
  /** False when the org has no GitHub App installation at all. */
  isGitHubConnected: boolean;
  /** Null while the suite has no property yet (create flow, before an app pick). */
  propertyId: string | null;
  selectedRepoId: string | null;
  onSelectRepo: (repoId: string) => void;
  /** Branch or tag; empty means "the repo's default branch". */
  repoRef: string;
  onRepoRefChange: (ref: string) => void;
}

/** Warning card shared by both "nothing to pick" states. */
function BlockedState({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box className="rounded-lg border border-warning/30 bg-warning/10 p-4">
      <Flex align="start" gap="sm">
        <Warning size={20} className="mt-0.5 shrink-0 text-warning" />
        <Box>
          <Text weight="medium">{title}</Text>
          <Text size="sm" className="mt-1 text-muted-foreground">
            {children}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}

/**
 * Pick the repository an external suite runs out of.
 *
 * @remarks Only ever a choice among repos already linked to this property, and
 * deliberately never a free-text URL field. The clone URL is derived server-side
 * from the linked row because it is passed to `git`, which treats
 * `ext::sh -c '…'` as a transport — a customer-supplied URL is a
 * command-execution surface, not a string. The picker's three empty states
 * (no installation, installed but nothing linked here, nothing selected) each
 * point at the one screen that fixes them; a single "no repos" message would
 * send two thirds of people to the wrong place.
 */
export function RepoPicker({
  repos,
  isGitHubConnected,
  propertyId,
  selectedRepoId,
  onSelectRepo,
  repoRef,
  onRepoRefChange,
}: RepoPickerProps) {
  const options = React.useMemo<SelectOption[]>(
    () =>
      repos.map((repo) => ({
        id: repo.id,
        label: repo.repoFullName,
        description: repo.defaultBranch ? `default branch: ${repo.defaultBranch}` : undefined,
      })),
    [repos]
  );

  const selected = repos.find((r) => r.id === selectedRepoId) ?? null;

  if (!isGitHubConnected) {
    return (
      <BlockedState title="GitHub is not connected">
        Cofactor clones your test repository through the GitHub App, so the app has to be
        installed on the organization that owns it first.{' '}
        {/* Same destination the property Code tab sends people to, so there is
            one place that means "connect GitHub". */}
        <Link to="/settings" search={{ tab: 'github' }} className="text-primary hover:underline">
          Connect GitHub in Settings
        </Link>
        .
      </BlockedState>
    );
  }

  if (repos.length === 0) {
    return (
      <BlockedState title="No repositories linked to this application">
        GitHub is connected, but no repository is linked to this application yet — and the
        suite can only clone one that is.{' '}
        {propertyId ? (
          <Link
            to="/applications/$propertyId/code"
            params={{ propertyId }}
            className="text-primary hover:underline"
          >
            Link one on the Code tab
          </Link>
        ) : (
          <>Link one on the application&rsquo;s Code tab</>
        )}
        .
      </BlockedState>
    );
  }

  return (
    <Flex direction="column" gap="md">
      <Select
        label="Repository"
        description="Only repositories linked to this application through the GitHub App can be used."
        placeholder="Choose a repository"
        items={options}
        selectedKey={selectedRepoId}
        onSelectionChange={(key) => onSelectRepo(String(key))}
      />
      <TextField
        label="Branch or tag"
        placeholder={selected?.defaultBranch ?? 'default branch'}
        description={
          selected?.defaultBranch
            ? `Leave blank to track ${selected.defaultBranch}.`
            : 'Leave blank to track the repository default branch.'
        }
        value={repoRef}
        onChange={onRepoRefChange}
      />
    </Flex>
  );
}
